import { Table, TableBody, TableCell, TableHead, TableRow, Typography, withStyles } from '@material-ui/core';
import { useEffect, useRef, useState } from 'react';
import { cancelToken } from '../../utils/axiosInstance';
import {
    fwEndpointStatusLabels,
    fwStartOutcomeLabels,
    fwStartOutcomeOrder,
    fwStartOutcomeRowLabels,
    fwTaskStatusColors,
    fwTaskStatusLabels,
} from '../../utils/constants';
import { formatFwDate } from '../../utils/fwHelpers';
import { probeWorkflow, readTaskStatus, refusalText, workflowModes } from '../../utils/fwWorkflow';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';

const START_CEILING = 200;

const failureLabel = code =>
    code ? fwEndpointStatusLabels[code] ?? fwEndpointStatusLabels.failed_other : '—';

const styles = {
    root: {
        marginBottom: '2rem',
    },
    header: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '2rem',
        alignItems: 'baseline',
        marginBottom: '0.5rem',
    },
    note: {
        marginBottom: '0.5rem',
    },
    counts: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '2rem',
        margin: '1rem 0',
    },
    count: {
        display: 'flex',
        flexDirection: 'column',
    },
    table: {
        marginTop: '1rem',
        fontFamily: 'inherit',
    },
};

const countOf = (outcomes, key) => {
    const value = outcomes?.[key];

    return Number.isInteger(value) ? value : 0;
};

const cycleText = cycle => {
    if (!cycle) return 'Zadanie nie ma otwartego cyklu.';

    return `Cykl ${cycle.sequence}: limit ${cycle.allowance}, rozpoczęte ${cycle.started}, pozostałe ${cycle.remaining}.`;
};

const FwTaskStarts = ({ classes, taskId }) => {
    const [mode, setMode] = useState(workflowModes.LOADING);
    const [status, setStatus] = useState(null);
    const [banner, setBanner] = useState(null);
    const requestSource = useRef(null);

    useEffect(() => {
        let active = true;
        requestSource.current?.cancel();
        requestSource.current = cancelToken.source();
        const config = { cancelToken: requestSource.current.token };

        probeWorkflow(config).then(result => {
            if (!active || result.mode === workflowModes.LOADING) return;
            if (result.mode !== workflowModes.CONFIGURED) {
                setMode(result.mode);
                setBanner(refusalText(result.refusal, 'Nie można odczytać startów zadania'));
                return;
            }
            setMode(workflowModes.CONFIGURED);
            readTaskStatus(taskId, config).then(read => {
                if (!active) return;
                if (read.state === 'read') {
                    setStatus(read.data);
                    return;
                }
                setMode(read.state);
                setBanner(refusalText(read.refusal, 'Nie można odczytać startów zadania'));
            });
        });

        return () => {
            active = false;
            requestSource.current?.cancel();
        };
    }, [taskId]);

    if (mode === workflowModes.LOADING) {
        return null;
    }

    if (mode === workflowModes.DISABLED) {
        return null;
    }

    if (mode === 'not_found') {
        return (
            <Typography className={classes.root} color="error">
                {`Nie znaleziono zadania ${taskId} w modelu odczytu.`}
            </Typography>
        );
    }

    if (mode !== workflowModes.CONFIGURED) {
        return (
            <Typography className={classes.root} color="error">
                {banner ?? 'Nie można odczytać startów zadania'}
            </Typography>
        );
    }

    if (!status) {
        return (
            <div className={classes.root}>
                <LoadingSpinner />
            </div>
        );
    }

    const starts = status.starts ?? [];
    const statusLabel = fwTaskStatusLabels[status.status];
    const statusColor = fwTaskStatusColors[status.status];
    const ceilingReached = Boolean(status.truncated) || starts.length >= START_CEILING;

    return (
        <div className={classes.root}>
            <Typography variant="h6">Pobrania FW zadania</Typography>
            <div className={classes.header}>
                <Typography style={statusColor ? { color: statusColor } : undefined}>
                    {statusLabel ?? `Status ${status.status}`}
                </Typography>
                {status.paused_reason && (
                    <Typography>{`Powód pauzy: ${status.paused_reason}`}</Typography>
                )}
                <Typography>{cycleText(status.cycle)}</Typography>
            </div>
            <div className={classes.counts}>
                {fwStartOutcomeOrder.map(key => (
                    <div
                        className={classes.count}
                        key={key}
                        role="group"
                        aria-label={fwStartOutcomeLabels[key]}>
                        <Typography variant="caption">{fwStartOutcomeLabels[key]}</Typography>
                        <Typography variant="h6">{countOf(status.outcomes, key)}</Typography>
                    </div>
                ))}
            </div>
            <Typography className={classes.note} variant="caption">
                {`Liczby sesji mogą się pokrywać, a instalacje liczone są na próbę. Razem sesji: ${
                    status.session_total ?? 0
                }.`}
            </Typography>
            {ceilingReached && (
                <Typography className={classes.note} variant="caption">
                    {`Lista startów jest ograniczona do ${START_CEILING} najnowszych sesji.`}
                </Typography>
            )}
            <Table className={classes.table} size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Próba</TableCell>
                        <TableCell>Sesja</TableCell>
                        <TableCell>Wynik</TableCell>
                        <TableCell>Błąd</TableCell>
                        <TableCell>Dostarczono (UTC)</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {starts.map(start => (
                        <TableRow key={start.session_id}>
                            <TableCell>{start.attempt_id}</TableCell>
                            <TableCell>{start.session_id}</TableCell>
                            <TableCell>{fwStartOutcomeRowLabels[start.outcome] ?? start.outcome}</TableCell>
                            <TableCell title={start.failure_code || undefined}>
                                {failureLabel(start.failure_code)}
                            </TableCell>
                            <TableCell>{formatFwDate(start.delivered_at)}</TableCell>
                        </TableRow>
                    ))}
                    {starts.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={5}>Brak startów dla tego zadania</TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </div>
    );
};

export default withStyles(styles)(FwTaskStarts);
